import { useCallback } from 'react';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib/constants';
import { fetchFanTags } from '@/lib/api/email-flows';
import { useStanQuery } from '@/queries';
import { ProductOption, Tag, TagSelectorMode } from '../types/audience.types';
import {
    buildTagListWithEveryone,
    buildTagListWithoutEveryone,
    handleEveryoneTagLogic,
} from '../logic/tagProcessor.logic';

interface UseTagManagementProps {
    mode: TagSelectorMode;
    selectedTags: Tag[];
    onTagsChange: (tags: Tag[]) => void;
    productOptions: ProductOption[];
}

export const useTagManagement = ({ mode, selectedTags, onTagsChange, productOptions }: UseTagManagementProps) => {
    const { data: apiTags = [], isLoading } = useStanQuery(
        {
            queryKey: [QUERY_KEYS.FAN_TAGS],
            queryFn: fetchFanTags,
            staleTime: 1000 * 60 * 10, // 10 minutes - tags don't change frequently
            meta: {
                error: {
                    sentryMessage: '[EMAIL_FLOWS] Failed to fetch fan tags',
                    title: 'Error Loading Tags',
                    message: 'Unable to load available tags. Please try again.',
                    feature: SENTRY_FEATURES.EMAIL_FLOWS,
                },
            },
        },
        true
    );

    // Convert product options to Tag format
    const productTags: Tag[] = productOptions.map(product => ({
        name: product.tag,
        type: 'pseudo' as const,
        data: product.data,
    }));

    // Build tag list based on mode
    const availableTags =
        mode === 'include'
            ? buildTagListWithEveryone(apiTags, productTags)
            : buildTagListWithoutEveryone(apiTags, productTags);

    const handleTagAdd = useCallback(
        (tag: Tag) => {
            // Handle "Everyone" tag logic for include mode
            if (mode === 'include') {
                const updatedTags = handleEveryoneTagLogic(tag, selectedTags);
                onTagsChange(updatedTags);
                return;
            }

            // Normal tag selection - add if not already selected
            if (!selectedTags.some(t => t.name === tag.name)) {
                onTagsChange([...selectedTags, tag]);
            }
        },
        [mode, selectedTags, onTagsChange]
    );

    const handleTagRemove = useCallback(
        (tagName: string) => {
            onTagsChange(selectedTags.filter(t => t.name !== tagName));
        },
        [selectedTags, onTagsChange]
    );

    // Filter out already selected tags from available options
    const filteredAvailableTags = availableTags.filter(tag => !selectedTags.some(t => t.name === tag.name));

    return {
        availableTags: filteredAvailableTags,
        handleTagAdd,
        handleTagRemove,
        isLoading,
    };
};
